/* eslint-disable */
import {
  SocketMessageInterface,
  HallCallsState,
  DoorState,
} from "../../interfaces/Monitoring.interface";
import PositionCallsStatusT from "../../types/PositionCallsStatus";
import { DeviceDirection } from "../../types/DeviceDirection";
import { ElevatorUsage } from "../generated/usage/elevator-usage";
import { EscalatorUsage } from "../generated/usage/escalator-usage";
import { ElevatorTraffic } from "../generated/traffic/elevator-traffic";
import { EscalatorTraffic } from "../generated/traffic/escalator-traffic";
import { ElevatorControl } from "../generated/control/elevator-control";
import { EscalatorControl } from "../generated/control/escalator-control";
import { ConnectionStatus } from "../generated/status/connection-status";
import { ElevatorFault } from "../generated/fault/elevator-fault";
import { EscalatorFault } from "../generated/fault/escalator-fault";
import { ElevatorService } from "../generated/service/elevator-service";
import { EscalatorService } from "../generated/service/escalator-service";
import { FaultMessageT } from "../generated/fault/fault-message";
import { ElevatorFaultMessageT } from "../generated/fault/elevator-fault-message";
import { EscalatorFaultMessageT } from "../generated/fault/escalator-fault-message";
import { FaultContent } from "../generated/fault/fault-content";
import { ServiceMessageT } from "../generated/service/service-message";
import { ElevatorServiceMessageT } from "../generated/service/elevator-service-message";
import { EscalatorServiceMessageT } from "../generated/service/escalator-service-message";
import { DeviceServiceContent } from "../generated/service/device-service-content";
import { ElevatorTrafficMessageT } from "../generated/traffic/elevator-traffic-message";
import { EscalatorTrafficMessageT } from "../generated/traffic/escalator-traffic-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { EscalatorControlMessageT } from "../generated/control/escalator-control-message";
import { ElevatorUsageMessageT } from "../generated/usage/elevator-usage-message";
import { EscalatorUsageMessageT } from "../generated/usage/escalator-usage-message";

import { FrontEndMessageT } from "../generated/front-end/front-end-message";
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { BitMaskMessageContent } from "../generated/ln2/bit-mask-message-content";
import { OneHotMessageContent } from "../generated/ln2/one-hot-message-content";
import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";
import { TrafficMessageT } from "../generated/traffic/traffic-message";
import { ControlMessageT } from "../generated/control/control-message";
import { UsageMessageT } from "../generated/usage/usage-message";
import { StatusMessageT } from "../generated/status/status-message";
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { DeviceUsageContent } from "../generated/usage/device-usage-content";

import { UuidT } from "../generated/front-end/uuid";
import {
  compoundKey,
  bitmaskPrefix,
  faultNumericKey,
} from "../../utils/typedKeys";
import { extractConnectionStatus } from "./frontEndMessageDecoder";

// ---------------------------------------------------------
// Default empty state for a new lift
// ---------------------------------------------------------
export function createDefaultState(
  bankId: UuidT,
  liftId: UuidT
): SocketMessageInterface {
  return {
    bankId,
    liftId,
    carCalls: new PositionCallsStatusT(),
    hallCalls: {
      up: new PositionCallsStatusT(),
      down: new PositionCallsStatusT(),
      upAssigned: new PositionCallsStatusT(),
      downAssigned: new PositionCallsStatusT(),
    },
    faults: false,
    activeFaults: [],
    position: 0,
    serviceMode: null,
    direction: null,
    doorStatus: {
      frontOpen: false,
      rearOpen: false,
      frontStalled: false,
      rearStalled: false,
    },
    doorZone: null,
    levelling: false,
    escalatorControl: null,
    connected: true,
    msg: {
      contentType: EncodedMessageContent.BitMaskMessage,
      messageType: BitMaskMessageContent.Status_StatusMessage,
      content: null,
      active: false,
    },
    bitmask: {},
    positionMap: {},
    serviceMap: {},
  };
}

// ---------------------------------------------------------
// Helpers for position arrays (add/remove floor numbers)
// ---------------------------------------------------------
function addToArray(arr: number[], position: number): number[] {
  if (arr.includes(position)) return arr;
  return [...arr, position].sort((a, b) => a - b);
}

function removeFromArray(arr: number[], position: number): number[] {
  if (!arr.includes(position)) return arr;
  return arr.filter((p) => p !== position);
}

function updatePositionArray(
  arr: number[],
  position: number,
  active: boolean
): number[] {
  return active ? addToArray(arr, position) : removeFromArray(arr, position);
}

// ---------------------------------------------------------
// Deep clone helpers for immutable updates
// ---------------------------------------------------------
function clonePositionCalls(pc: PositionCallsStatusT): PositionCallsStatusT {
  return new PositionCallsStatusT(
    [...pc.front],
    [...pc.rear],
    [...pc.frontLock],
    [...pc.rearLock]
  );
}

function cloneHallCallsState(hc: HallCallsState): HallCallsState {
  return {
    up: clonePositionCalls(hc.up),
    down: clonePositionCalls(hc.down),
    upAssigned: clonePositionCalls(hc.upAssigned),
    downAssigned: clonePositionCalls(hc.downAssigned),
  };
}

// ---------------------------------------------------------
// Apply a traffic delta — switch on ElevatorTraffic enum
// ---------------------------------------------------------
function applyTrafficDelta(
  state: SocketMessageInterface,
  update: ElevatorTrafficMessageT,
  active: boolean
): SocketMessageInterface {
  const code = update.message;
  const position = update.position;

  // Position update is a direct set, not add/remove
  if (code === ElevatorTraffic.P) {
    const posKey = compoundKey(state.bankId, state.liftId);
    if (state.position === position && state.positionMap[posKey] === position)
      return state;
    return {
      ...state,
      position,
      positionMap: { ...state.positionMap, [posKey]: position },
    };
  }

  // carCalls fields — only clone carCalls
  if (
    code === ElevatorTraffic.CarCall ||
    code === ElevatorTraffic.RearCarCall
  ) {
    const newCarCalls = clonePositionCalls(state.carCalls);
    if (code === ElevatorTraffic.CarCall) {
      newCarCalls.front = updatePositionArray(
        newCarCalls.front,
        position,
        active
      );
    } else {
      newCarCalls.rear = updatePositionArray(
        newCarCalls.rear,
        position,
        active
      );
    }
    return { ...state, carCalls: newCarCalls };
  }

  // hallCalls fields — only clone hallCalls
  const newHallCalls = cloneHallCallsState(state.hallCalls);
  switch (code) {
    case ElevatorTraffic.HallCall:
    case ElevatorTraffic.UpHallCall:
      newHallCalls.up.front = updatePositionArray(
        newHallCalls.up.front,
        position,
        active
      );
      break;
    case ElevatorTraffic.UpRearHallCall:
      newHallCalls.up.rear = updatePositionArray(
        newHallCalls.up.rear,
        position,
        active
      );
      break;
    case ElevatorTraffic.DownHallCall:
      newHallCalls.down.front = updatePositionArray(
        newHallCalls.down.front,
        position,
        active
      );
      break;
    case ElevatorTraffic.DownRearHallCall:
      newHallCalls.down.rear = updatePositionArray(
        newHallCalls.down.rear,
        position,
        active
      );
      break;
    default:
      return state;
  }

  return { ...state, hallCalls: newHallCalls };
}

// ---------------------------------------------------------
// Apply a control delta — switch on ElevatorControl enum
// ---------------------------------------------------------
function applyControlDelta(
  state: SocketMessageInterface,
  update: ElevatorControlMessageT,
  active: boolean
): SocketMessageInterface {
  const code = update.message;
  const position = update.position;

  // carCalls lock fields — only clone carCalls
  if (
    code === ElevatorControl.CarCallLock ||
    code === ElevatorControl.CarCallUnlock ||
    code === ElevatorControl.RearCarCallLock ||
    code === ElevatorControl.RearCarCallUnlock
  ) {
    const newCarCalls = clonePositionCalls(state.carCalls);
    if (
      code === ElevatorControl.CarCallLock ||
      code === ElevatorControl.CarCallUnlock
    ) {
      newCarCalls.frontLock = updatePositionArray(
        newCarCalls.frontLock,
        position,
        active
      );
    } else {
      newCarCalls.rearLock = updatePositionArray(
        newCarCalls.rearLock,
        position,
        active
      );
    }
    return { ...state, carCalls: newCarCalls };
  }

  // hallCalls fields — only clone hallCalls
  const newHallCalls = cloneHallCallsState(state.hallCalls);
  switch (code) {
    case ElevatorControl.UpHallCallLock:
    case ElevatorControl.UpHallCallUnlock:
      newHallCalls.up.frontLock = updatePositionArray(
        newHallCalls.up.frontLock,
        position,
        active
      );
      break;
    case ElevatorControl.UpRearHallCallLock:
    case ElevatorControl.UpRearHallCallUnlock:
      newHallCalls.up.rearLock = updatePositionArray(
        newHallCalls.up.rearLock,
        position,
        active
      );
      break;
    case ElevatorControl.DownHallCallLock:
    case ElevatorControl.DownHallCallUnlock:
      newHallCalls.down.frontLock = updatePositionArray(
        newHallCalls.down.frontLock,
        position,
        active
      );
      break;
    case ElevatorControl.DownRearHallCallLock:
    case ElevatorControl.DownRearHallCallUnlock:
      newHallCalls.down.rearLock = updatePositionArray(
        newHallCalls.down.rearLock,
        position,
        active
      );
      break;
    case ElevatorControl.UpAssignedHallCall:
      newHallCalls.upAssigned.front = updatePositionArray(
        newHallCalls.upAssigned.front,
        position,
        active
      );
      break;
    case ElevatorControl.UpAssignedRearHallCall:
      newHallCalls.upAssigned.rear = updatePositionArray(
        newHallCalls.upAssigned.rear,
        position,
        active
      );
      break;
    case ElevatorControl.DownAssignedHallCall:
      newHallCalls.downAssigned.front = updatePositionArray(
        newHallCalls.downAssigned.front,
        position,
        active
      );
      break;
    case ElevatorControl.DownAssignedRearHallCall:
      newHallCalls.downAssigned.rear = updatePositionArray(
        newHallCalls.downAssigned.rear,
        position,
        active
      );
      break;
    default:
      return state;
  }

  return { ...state, hallCalls: newHallCalls };
}

// ---------------------------------------------------------
// Apply an escalator control delta
// ---------------------------------------------------------
function applyEscalatorControlDelta(
  state: SocketMessageInterface,
  update: EscalatorControlMessageT,
  active: boolean
): SocketMessageInterface {
  const newValue: EscalatorControl | null = active ? update.message : null;
  if (state.escalatorControl === newValue) return state;
  return { ...state, escalatorControl: newValue };
}

// ---------------------------------------------------------
// Direction code sets
// ---------------------------------------------------------
const ELEVATOR_DIRECTION_CODES = new Set([
  ElevatorUsage.Up,
  ElevatorUsage.Down,
  ElevatorUsage.Forward,
  ElevatorUsage.Backward,
  ElevatorUsage.Moving,
  ElevatorUsage.RunUp,
  ElevatorUsage.RunDown,
]);

const ELEVATOR_IDLE_CODES = new Set([
  ElevatorUsage.Parked,
  ElevatorUsage.NormalStopped,
  ElevatorUsage.FaultedStopped,
]);

const ESCALATOR_IDLE_CODES = new Set([
  EscalatorUsage.FaultStopped,
  EscalatorUsage.Stopped,
  EscalatorUsage.Disconnected,
]);

// ---------------------------------------------------------
// Apply a usage delta — switch on ElevatorUsage enum
// ---------------------------------------------------------
function applyUsageDelta(
  state: SocketMessageInterface,
  update: ElevatorUsageMessageT,
  active: boolean
): SocketMessageInterface {
  const code = update.message;

  // Direction codes
  if (ELEVATOR_DIRECTION_CODES.has(code) || ELEVATOR_IDLE_CODES.has(code)) {
    const directionValue: DeviceDirection =
      active && !ELEVATOR_IDLE_CODES.has(code) ? code : null;
    return { ...state, direction: directionValue };
  }

  // Front door open
  if (
    code === ElevatorUsage.FrontDoorOpened ||
    code === ElevatorUsage.FrontDoorOpening
  ) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      frontOpen: active,
    };
    return { ...state, doorStatus: newDoor };
  }

  // Front door close
  if (
    code === ElevatorUsage.FrontDoorClosed ||
    code === ElevatorUsage.FrontDoorClosing
  ) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      frontOpen: !active,
    };
    return { ...state, doorStatus: newDoor };
  }

  // Rear door open
  if (
    code === ElevatorUsage.RearDoorOpened ||
    code === ElevatorUsage.RearDoorOpening
  ) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      rearOpen: active,
    };
    return { ...state, doorStatus: newDoor };
  }

  // Rear door close
  if (
    code === ElevatorUsage.RearDoorClosed ||
    code === ElevatorUsage.RearDoorClosing
  ) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      rearOpen: !active,
    };
    return { ...state, doorStatus: newDoor };
  }

  // Door zone
  if (
    code === ElevatorUsage.AboveDoorZone ||
    code === ElevatorUsage.InDoorZone ||
    code === ElevatorUsage.BelowDoorZone
  ) {
    const newZone = active ? code : null;
    if (state.doorZone === newZone) return state;
    return { ...state, doorZone: newZone };
  }

  // Door stalled
  if (code === ElevatorUsage.FrontDoorStalled) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      frontStalled: active,
    };
    return { ...state, doorStatus: newDoor };
  }
  if (code === ElevatorUsage.RearDoorStalled) {
    const newDoor: DoorState = {
      ...state.doorStatus,
      rearStalled: active,
    };
    return { ...state, doorStatus: newDoor };
  }

  // Levelling
  if (code === ElevatorUsage.Levelling) {
    if (state.levelling === active) return state;
    return { ...state, levelling: active };
  }

  return state;
}

// ---------------------------------------------------------
// Apply an escalator usage delta
// ---------------------------------------------------------
function applyEscalatorUsageDelta(
  state: SocketMessageInterface,
  update: EscalatorUsageMessageT,
  active: boolean
): SocketMessageInterface {
  if (!active) return state;
  const code = update.message;
  const directionValue: DeviceDirection = ESCALATOR_IDLE_CODES.has(code)
    ? null
    : code;
  if (state.direction === directionValue) return state;
  return { ...state, direction: directionValue };
}

// ---------------------------------------------------------
// Apply a fault delta
// ---------------------------------------------------------
function applyFaultDelta(
  state: SocketMessageInterface,
  update: FaultMessageT,
  active: boolean
): SocketMessageInterface {
  const fk = faultNumericKey(update.faultType, update.fault?.message ?? 0);
  const faultMap = new Map(
    state.activeFaults.map((f) => [
      faultNumericKey(f.faultType, f.fault?.message ?? 0),
      f,
    ])
  );

  if (active) {
    if (faultMap.has(fk)) return state;
    faultMap.set(fk, update);
  } else {
    if (!faultMap.has(fk)) return state;
    faultMap.delete(fk);
  }

  return {
    ...state,
    activeFaults: Array.from(faultMap.values()),
  };
}

// ---------------------------------------------------------
// Apply a service delta
// ---------------------------------------------------------
function applyServiceDelta(
  state: SocketMessageInterface,
  update: ServiceMessageT,
  active: boolean,
  serviceName: string
): SocketMessageInterface {
  const newMode = active ? update : null;

  if (
    state.serviceMode?.messageType === newMode?.messageType &&
    state.serviceMode?.message?.message === newMode?.message?.message
  ) {
    return state;
  }

  // Update serviceMap with compound key
  const svcKey = compoundKey(state.bankId, state.liftId);
  const activeName = active ? serviceName : "";
  const updatedServiceMap = { ...state.serviceMap };
  if (activeName) {
    updatedServiceMap[svcKey] = activeName;
  } else {
    delete updatedServiceMap[svcKey];
  }

  return {
    ...state,
    serviceMode: newMode,
    serviceMap: updatedServiceMap,
  };
}

// ---------------------------------------------------------
// Display map accumulation modes
// ---------------------------------------------------------
export type DisplayMapMode = "accumulate" | "last_wins";

const usageName = (v: ElevatorUsage): string => ElevatorUsage[v];

const EXCLUSIVE_GROUPS: Record<string, string[]> = {
  [`Usage:${usageName(ElevatorUsage.Up)}`]: [
    `Usage:${usageName(ElevatorUsage.Down)}`,
  ],
  [`Usage:${usageName(ElevatorUsage.Down)}`]: [
    `Usage:${usageName(ElevatorUsage.Up)}`,
  ],
  [`Usage:${usageName(ElevatorUsage.FrontDoorOpened)}`]: [
    `Usage:${usageName(ElevatorUsage.FrontDoorClosed)}`,
  ],
  [`Usage:${usageName(ElevatorUsage.FrontDoorClosed)}`]: [
    `Usage:${usageName(ElevatorUsage.FrontDoorOpened)}`,
  ],
  [`Usage:${usageName(ElevatorUsage.RearDoorOpened)}`]: [
    `Usage:${usageName(ElevatorUsage.RearDoorClosed)}`,
  ],
  [`Usage:${usageName(ElevatorUsage.RearDoorClosed)}`]: [
    `Usage:${usageName(ElevatorUsage.RearDoorOpened)}`,
  ],
};

// ---------------------------------------------------------
// Derive meta { type, value } from the inner fault message
// ---------------------------------------------------------
function deriveFaultValue(faultMsg: FaultMessageT): string {
  switch (faultMsg.faultType) {
    case FaultContent.ElevatorFaultMessage:
      return (
        ElevatorFault[(faultMsg.fault as ElevatorFaultMessageT)?.message] ?? ""
      );
    case FaultContent.EscalatorFaultMessage:
      return (
        EscalatorFault[(faultMsg.fault as EscalatorFaultMessageT)?.message] ??
        ""
      );
    default:
      return "";
  }
}

// ---------------------------------------------------------
// Derive meta { type, value } from the inner service message
// ---------------------------------------------------------
function deriveServiceName(serviceMsg: ServiceMessageT): string {
  switch (serviceMsg.messageType) {
    case DeviceServiceContent.ElevatorServiceMessage:
      return (
        ElevatorService[
          (serviceMsg.message as ElevatorServiceMessageT)?.message
        ] ?? ""
      );
    case DeviceServiceContent.EscalatorServiceMessage:
      return (
        EscalatorService[
          (serviceMsg.message as EscalatorServiceMessageT)?.message
        ] ?? ""
      );
    default:
      return "";
  }
}

// ---------------------------------------------------------
// Bitmask-key label lookups from the union discriminators
// ---------------------------------------------------------
const BITMASK_TYPE_LABEL: Partial<Record<BitMaskMessageContent, string>> = {
  [BitMaskMessageContent.Traffic_TrafficMessage]: "Traffic",
  [BitMaskMessageContent.Control_ControlMessage]: "Control",
  [BitMaskMessageContent.Fault_FaultMessage]: "Fault",
  [BitMaskMessageContent.Status_StatusMessage]: "Status",
  [BitMaskMessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage]:
    "GroupSupervisoryStatus",
  [BitMaskMessageContent.GroupControl_GroupControlMessage]: "GroupControl",
  [BitMaskMessageContent.ControllerInfo_ControllerInfoMessage]:
    "ControllerInfo",
};

const ONEHOT_TYPE_LABEL: Partial<Record<OneHotMessageContent, string>> = {
  [OneHotMessageContent.Usage_UsageMessage]: "Usage",
  [OneHotMessageContent.Service_ServiceMessage]: "Service",
};

/** Convert the union discriminators to the string label used in bitmask keys. */
export function msgTypeLabel(
  contentType: EncodedMessageContent,
  messageType: BitMaskMessageContent | OneHotMessageContent
): string {
  if (contentType === EncodedMessageContent.BitMaskMessage)
    return BITMASK_TYPE_LABEL[messageType as BitMaskMessageContent] ?? "";
  if (contentType === EncodedMessageContent.OneHotMessage)
    return ONEHOT_TYPE_LABEL[messageType as OneHotMessageContent] ?? "";
  return "";
}

// ---------------------------------------------------------
// Resolve the leaf enum name from the inner union message
// ---------------------------------------------------------
export function resolveValue(encoded: {
  contentType: EncodedMessageContent;
  content: BitMaskMessageT | OneHotMessageT | null;
}): string {
  if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
    const bm = encoded.content as BitMaskMessageT;
    switch (bm.messageType) {
      case BitMaskMessageContent.Traffic_TrafficMessage: {
        const t = bm.message as TrafficMessageT;
        if (t.messageType === DeviceTrafficMessage.ElevatorTrafficMessage)
          return (
            ElevatorTraffic[(t.message as ElevatorTrafficMessageT).message] ??
            ""
          );
        if (t.messageType === DeviceTrafficMessage.EscalatorTrafficMessage)
          return (
            EscalatorTraffic[(t.message as EscalatorTrafficMessageT).message] ??
            ""
          );
        break;
      }
      case BitMaskMessageContent.Control_ControlMessage: {
        const c = bm.message as ControlMessageT;
        if (c.controlType === DeviceControlMessage.ElevatorControlMessage)
          return (
            ElevatorControl[(c.control as ElevatorControlMessageT).message] ??
            ""
          );
        if (c.controlType === DeviceControlMessage.EscalatorControlMessage)
          return (
            EscalatorControl[(c.control as EscalatorControlMessageT).message] ??
            ""
          );
        break;
      }
      case BitMaskMessageContent.Fault_FaultMessage:
        return deriveFaultValue(bm.message as FaultMessageT);
      case BitMaskMessageContent.Status_StatusMessage: {
        const conn = extractConnectionStatus(bm.message as StatusMessageT);
        return conn ? ConnectionStatus[conn.message] ?? "" : "";
      }
      default:
        break;
    }
  }

  if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    const oh = encoded.content as OneHotMessageT;
    switch (oh.messageType) {
      case OneHotMessageContent.Usage_UsageMessage: {
        const u = oh.message as UsageMessageT;
        if (u.messageType === DeviceUsageContent.ElevatorUsageMessage)
          return (
            ElevatorUsage[(u.message as ElevatorUsageMessageT).message] ?? ""
          );
        if (u.messageType === DeviceUsageContent.EscalatorUsageMessage)
          return (
            EscalatorUsage[(u.message as EscalatorUsageMessageT).message] ?? ""
          );
        break;
      }
      case OneHotMessageContent.Service_ServiceMessage:
        return deriveServiceName(oh.message as ServiceMessageT);
      default:
        break;
    }
  }

  return "";
}

// ---------------------------------------------------------
// MAIN: Apply a FrontEndMessageT to current state
// ---------------------------------------------------------
export function applyDeltaToState(
  currentState: SocketMessageInterface,
  fem: FrontEndMessageT,
  displayMapMode?: DisplayMapMode
): SocketMessageInterface {
  const active = fem.msg!.active;
  const encoded = fem.msg!.msg!;
  let newState: SocketMessageInterface;
  let isService = false;

  switch (encoded.contentType) {
    case EncodedMessageContent.BitMaskMessage: {
      const bm = encoded.content as BitMaskMessageT;
      switch (bm.messageType) {
        case BitMaskMessageContent.Traffic_TrafficMessage: {
          const traffic = bm.message as TrafficMessageT;
          switch (traffic.messageType) {
            case DeviceTrafficMessage.ElevatorTrafficMessage: {
              const inner = traffic.message as ElevatorTrafficMessageT;
              newState = applyTrafficDelta(currentState, inner, active);
              break;
            }
            case DeviceTrafficMessage.EscalatorTrafficMessage: {
              const inner = traffic.message as EscalatorTrafficMessageT;
              newState = currentState; // escalator traffic is bitmask-only
              break;
            }
            default:
              newState = currentState;
          }
          break;
        }
        case BitMaskMessageContent.Control_ControlMessage: {
          const control = bm.message as ControlMessageT;
          switch (control.controlType) {
            case DeviceControlMessage.ElevatorControlMessage: {
              const inner = control.control as ElevatorControlMessageT;
              newState = applyControlDelta(currentState, inner, active);
              break;
            }
            case DeviceControlMessage.EscalatorControlMessage: {
              const inner = control.control as EscalatorControlMessageT;
              newState = applyEscalatorControlDelta(
                currentState,
                inner,
                active
              );
              break;
            }
            default:
              newState = currentState;
          }
          break;
        }
        case BitMaskMessageContent.Fault_FaultMessage: {
          const faultMsg = bm.message as FaultMessageT;
          newState = applyFaultDelta(currentState, faultMsg, active);
          break;
        }
        case BitMaskMessageContent.Status_StatusMessage: {
          const statusMsg = bm.message as StatusMessageT;
          const connMsg = extractConnectionStatus(statusMsg);
          if (connMsg) {
            const isConnected =
              connMsg.message === ConnectionStatus.Connected && active;
            newState =
              currentState.connected === isConnected
                ? currentState
                : { ...currentState, connected: isConnected };
          } else {
            newState = currentState;
          }
          break;
        }
        // GSS, GC, CI — pass through for bitmask only (no state mutation)
        case BitMaskMessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage:
        case BitMaskMessageContent.GroupControl_GroupControlMessage:
        case BitMaskMessageContent.ControllerInfo_ControllerInfoMessage:
          newState = currentState;
          break;
        default:
          newState = currentState;
      }
      break;
    }
    case EncodedMessageContent.OneHotMessage: {
      const oh = encoded.content as OneHotMessageT;
      switch (oh.messageType) {
        case OneHotMessageContent.Usage_UsageMessage: {
          const usage = oh.message as UsageMessageT;
          switch (usage.messageType) {
            case DeviceUsageContent.ElevatorUsageMessage: {
              const inner = usage.message as ElevatorUsageMessageT;
              newState = applyUsageDelta(currentState, inner, active);
              break;
            }
            case DeviceUsageContent.EscalatorUsageMessage: {
              const inner = usage.message as EscalatorUsageMessageT;
              newState = applyEscalatorUsageDelta(currentState, inner, active);
              break;
            }
            default:
              newState = currentState;
          }
          break;
        }
        case OneHotMessageContent.Service_ServiceMessage: {
          isService = true;
          const serviceMsg = oh.message as ServiceMessageT;
          const serviceName = deriveServiceName(serviceMsg);
          newState = applyServiceDelta(
            currentState,
            serviceMsg,
            active,
            serviceName
          );
          break;
        }
        default:
          newState = currentState;
      }
      break;
    }
    default:
      newState = currentState;
      break;
  }

  // Read the union discriminators directly
  const contentType = encoded.contentType;
  const content = encoded.content as BitMaskMessageT | OneHotMessageT | null;
  const messageType =
    contentType === EncodedMessageContent.BitMaskMessage
      ? (content as BitMaskMessageT).messageType
      : contentType === EncodedMessageContent.OneHotMessage
      ? (content as OneHotMessageT).messageType
      : (0 as BitMaskMessageContent);

  // Update msg if the discriminators or active flag changed
  const msgChanged =
    newState.msg.contentType !== contentType ||
    newState.msg.messageType !== messageType ||
    newState.msg.content !== content ||
    newState.msg.active !== active;

  const updatedMsg = msgChanged
    ? { contentType, messageType, content, active }
    : newState.msg;

  // Only update bitmask if a new entry differs from existing.
  // Skip service deltas — service info is stored in serviceMap only.
  let updatedBitmask = newState.bitmask;
  const typeLabel = msgTypeLabel(contentType, messageType);
  const value = resolveValue(encoded);
  if (value && !isService) {
    const suffix = `${typeLabel}:${value}`;
    const prefix = bitmaskPrefix(newState.bankId, newState.liftId);
    const compoundBitmaskKey = `${prefix}${suffix}`;
    if (newState.bitmask[compoundBitmaskKey] !== active) {
      updatedBitmask = {
        ...newState.bitmask,
        [compoundBitmaskKey]: active,
      };

      // In "last_wins" mode, clear mutually exclusive peers when setting active
      if (displayMapMode === "last_wins" && active) {
        const peers = EXCLUSIVE_GROUPS[suffix];
        if (peers) {
          peers.forEach((peer) => {
            updatedBitmask[`${prefix}${peer}`] = false;
          });
        }
      }
    }
  }

  // Return current state reference if nothing changed
  if (!msgChanged && updatedBitmask === newState.bitmask) {
    return newState;
  }

  return {
    ...newState,
    msg: updatedMsg,
    bitmask: updatedBitmask,
  };
}
